import { AddIcon, Button, Dialog, Flex, ThemePrepared } from '@fluentui/react-northstar';
import _, { clone } from 'lodash';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilledFileIcon from '../../../src/svg/filled-file-icon.svg';
import LifecycleIcon from '../../../src/svg/lifecycle-dialog-icon.svg';
import AppCSS from '../App.module.css';
import { appState, AppStateValue, useSetState } from '../AppState';
import { TikitConfigState } from '../config/TikitConfiguration';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { EditTicketCustomFormAnswers } from '../shared/components/EditTicketCustomFormAnswers/EditTicketCustomFormAnswers';
import { getRelativeDateValue } from '../shared/components/EditTicketForm';
import TicketForm, {
  ActionTypes,
  entityTicketModel,
  entityTicketReducer,
  TicketActions,
  UnitOfWork,
} from '../shared/components/TicketForm';
import { addTicketMissingRequired } from '../shared/components/addTicketRequired';
import { usePeoplePickerTyped } from '../shared/hooks/usePeoplePickerTyped';
import { AddOrUpdateResolutionToDraftKB } from '../shared/components/TicketResolutionNoteComponents/ResolutionHelper';
import { formatInputRawData, getCardDateInputISOFormatted, mapCardAnswers } from '../shared/components/TicketTemplateAnswer';
import { Toast } from '../shared/components/toast/Toast';
import { Ticket } from '../shared/interfaces/ticket.interface';
import { platformService } from '../shared/services/platform.service';
import { ADMINISTRATORS, ANALYSTS, ConfigurationName, FILTERS_STRINGS, KNOWLEDGEAGENT, LICENSE_FEATURE, STATUS_GUID } from '../shared/utils/constants';
import { CustomFormAttachmentsProps } from '../templates/CustomForms/CustomForm.Interface';
import { buildCardAnswerJSON } from '../templates/CustomForms/CustomForm.Service';
import { SEND_FORM_OPTION } from '../templates/CustomForms/constants';
import { TemplateListDialog } from '../templates/TemplateListDialog';
import { SortWithDefaultTemplateFirst } from '../templates/TicketTemplatesTable';
import { ListContext } from './List/ListContextProvider';
import ListFunctions from './List/ListFunctions';
import { getStatusIdByGuid, toolbarButtonColor } from './ticketHelper';
import { CustomViewContext } from './toolbar/CustomViewContextProvider';
import { RefreshIcon } from '../shared/components/RefreshIcon';

const api = new platformService();
interface Props {
  globalTheme: ThemePrepared<any>;
  page: string;
  isUserLicensed: boolean;
}

export const AddTicketDialog = ({ globalTheme, page, isUserLicensed }: Props) => {
  const currentState = appState();
  const { t } = useTranslation();
  const [showAddTicketDialog, setShowAddTicketDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openTemplateList, setOpenTemplateList] = useState(false);
  const setState = useSetState();
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);
  const { _getpaginatedTicketsPromise } = ListFunctions();
  const [ticketData, dispatch] = useReducer(entityTicketReducer, entityTicketModel());
  const [templateList, setTemplateList] = useState([]);
  const [templateListLoaded, setTemplateListLoaded] = useState(false);
  const [dataSourceList, setDataSourceList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>({});
  const [showResolvedDialog, setShowResolvedDialog] = useState(false);
  const [tikitConfig, setTikitConfig] = useState<TikitConfiguration>(TikitConfigState);
  const [resolutionNoteEnabled, setResolutionNoteEnabled] = useState(false);
  const [isResolutionRole, setIsResolutionRole] = useState(false);
  const [isGroupChatEnabled, setIsGroupChatEnabled] = useState(false);
  const [resolvedStatusId, setResolvedStatusId] = useState(0);

  const [isCustomFormAnswersAccordionOpen, setIsCustomFormAnswersAccordionOpen] = useState(false);
  const [isCustomPropertiesEnabled, setIsCustomPropertiesEnabled] = useState(true);

  const loadCustomProperties = async () => {
    if ((currentState?.customProperties?.length ?? 0) === 0) {
      const result = await api.getCustomProperties(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}&${FILTERS_STRINGS.CUSTOM_PROPERTY_LIST}&$orderby=Order`);
      setState((prev: AppStateValue) => ({ ...prev, customProperties: result.data.value }));
    }
  };

  useEffect(() => {
    setResolvedStatusId(getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.RESOLVED));
    setIsResolutionRole(currentState.userRoles.roles.some(x => [ADMINISTRATORS, ANALYSTS, KNOWLEDGEAGENT].includes(x)));
  }, []);
  const [createGroupChat, setCreateGroupChat] = useState(false);  // Do not modify this or set it without checking with someone. On true it can fire a submit.  A.Altemus 7/11/2025

  useEffect(() => {
    const checkCustomPropertiesFeature = async () => {
      const isEnabled = await getCachedFeature(LICENSE_FEATURE.CustomProperties);
      setIsCustomPropertiesEnabled(isEnabled);
    };
    checkCustomPropertiesFeature();
  }, []);

  const onOpen = async () => {
    await loadCustomProperties();
    setSelectedTemplate({});
    dispatch(TicketActions.ClearData);
    dispatch({ action: TicketActions.ResetForm, data: true });

    const results = await Promise.all([
      api.getTicketTemplates(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}%20&$expand=Lifecycle($expand=Phases),TemplateForms($expand=Form),Tags,AffectedUsers,Collaborators&$orderby=Name`),
      api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
    ]);
    const templateResults = SortWithDefaultTemplateFirst(results[0].data.value);
    setTemplateList(templateResults);
    setTemplateListLoaded(true);
    setDataSourceList(results[1].data.value);


    const [resolutionConfig, resolutionEnabled] = await Promise.all([
      api.getSystemSetting(ConfigurationName.Resolution).then(response => ({
        ...response.data,
        IsResolutionNoteEnabled: response.data.IsEnabled,
      })),
      getCachedFeature(LICENSE_FEATURE.ResolutionNote),
    ]);

    setTikitConfig(resolutionConfig);
    setResolutionNoteEnabled(resolutionEnabled);
  };

  useEffect(() => {
    const getFeaturesEnabled = async () => {
      setIsGroupChatEnabled(await getCachedFeature(LICENSE_FEATURE.GroupChat));
    };
    getFeaturesEnabled();
  }, []);

  useEffect(() => {
    if (createGroupChat) {
      submit();
      setCreateGroupChat(false);
    }
  }, [createGroupChat]);

  /** Create stays off until every required field is filled (Shaun, 2026-10-20); each says so under itself once touched. */
  const affectedUsersTyped = usePeoplePickerTyped('mgt-affected-users');
  const missingRequired = addTicketMissingRequired(ticketData, currentState.teams, currentState.supportgroups, currentState.customProperties) || affectedUsersTyped;
  const validateRequired = () => missingRequired;

  const skeletonLoading = (transactionId: any, entity: any) => {
    const ticket = {
      ...entity,
      Id: transactionId,
      Requester: { Value: '' },
      Status: { Value: '' },
      Priority: { Value: '' },
      Category: { Value: '' },
      Title: `temp`,
      TicketSlas: [],
      TicketCollaborators: []
    };

    if (page === 'board') {
      setState((_state: AppStateValue) => {
        // create new reference to heap to make sure state was changed.
        const tickets: Ticket[] = _state.tickets.filter(() => true);
        tickets.unshift(ticket);

        return {
          ..._state,
          tickets: tickets,
          reloadBoardOnAddTicket: true
        };
      });
    } else {
      setListStates((prev: any) => {
        let updatedTickets = listStates.tickets;
        updatedTickets.unshift(ticket);

        return {
          ...prev,
          tickets: updatedTickets,
          countTickets: prev.countTickets + 1
        };
      });
    }
  };

  const removeSkeletonLoading = (transactionId: number) => {
    if (page === 'board') {
      setState((_state: AppStateValue) => {
        // create new reference to heap to make sure state was changed.
        const tickets: Ticket[] = _state.tickets.filter(
          ({ Id }) => Id !== transactionId
        );

        return {
          ..._state,
          tickets: tickets
        };
      });
    } else {
      setListStates((prev: any) => {
        // create new reference to heap to make sure state was changed.
        const tickets: Ticket[] = prev.tickets.filter(
          ({ Id }) => Id !== transactionId
        );

        return {
          ...prev,
          tickets: tickets,
        };
      });
    }
  };

  const updateBoard = async (
    ticket: Ticket,
    user: any,
    transactionId: number
  ) => {
    const _ticketsApiFunction =
      page === 'board' ? api.getTicketList(`?$orderby=CreatedDate desc`) : _getpaginatedTicketsPromise({ page: listStates.currentPage, count: listStates.countPerPage, customViewStates });
    try {
      const result = await Promise.all([
        api.getTicketSla(FILTERS_STRINGS.TICKET_SLA_FILTER),
        _ticketsApiFunction
      ]);
      const ticketsSla: TicketSla[] = result[0];

      if (page === 'board') {
        const [closed, active] = _.partition(result[1].data.value, (item) => item.Closed);
        setState((prev: AppStateValue) => {
          let thisTickets = active.map((t) => ({
            ...t,
            TicketSlas: ticketsSla.filter((sla) => sla.TicketId === t.Id),
          }));
          let thisClosed = closed.map((t) => ({
            ...t,
            TicketSlas: ticketsSla.filter((sla) => sla.TicketId === t.Id),
          }));

          if (ticket.Closed) {
            thisClosed = thisClosed.filter(({ Id }) => Id !== transactionId);
            thisClosed.unshift(ticket);
          } else {
            thisTickets = thisTickets.filter(({ Id }) => Id !== transactionId);
            thisTickets.unshift(ticket);
          }
          return {
            ...prev,
            tickets: thisTickets,
            closed: thisClosed,
            reloadBoardOnAddTicket: true
          };
        });
      } else {
        const updatedTickets = result[1].data;
        setListStates((prev) => {
          const tickets: Ticket[] = prev.tickets.filter(
            ({ Id }) => Id !== transactionId
          );
          tickets.unshift(ticket);

          return {
            ...prev,
            countTickets: updatedTickets['@odata.count'],
            tickets: updatedTickets.value.map((t) => ({
              ...t,
              TicketSlas: ticketsSla.filter((sla) => sla.TicketId === t.Id),
            })),
          };
        });
      }
    } catch (err) {
      console.error(err);
      errorHandler(transactionId);
    }
    setIsSaving(false);
  };

  const errorHandler = (transactionId) => {
    removeSkeletonLoading(transactionId);
    Toast.error(t('ticket-details.edit-ticket-view.failed-to-create'));
    setIsSaving(false);
  };

  const submit = async () => {
    if (resolutionNoteEnabled && tikitConfig.IsResolutionNoteEnabled && ticketData.SelectedEnums.StatusId.Id == resolvedStatusId) {
      ticketData.Entity.Resolution = { Note: "", InternalNote: "", ResolutionCategoryId: 0, ResolutionCategory: null, DraftKB: false, AnswerId: null };
      setShowResolvedDialog(true);
    }
    else await createTicket();
  };

  const createTicket = async () => {
    if (validateRequired()) return;
    setShowAddTicketDialog(false);

    let ticketId:any = null;

    const transactionId = Math.random();
    setIsSaving(true);
    skeletonLoading(transactionId, ticketData.Entity);

    const customForms = ticketData.Entity.CustomForms;
    const affectedUsers = ticketData.Entity.AffectedUsers;
    const collaboratorsEmails = ticketData.Entity.Collaborators;

    let cardFileAttachments: CustomFormAttachmentsProps[] = [];
    let cardFormAnswers: string = "";
    let updateCFviaFileUpload = false;
    let updateCFAnswers = false;
    
    if(ticketData.Entity.CustomForms){
      const customForms = ticketData.Entity.CustomForms;
      customForms?.forEach(form => {
        let cfFormAnswer = "";
        if(form.CardAnswerJson && form.CardAnswerJson.length > 0){
          cfFormAnswer = form.CardAnswerJson;
          if(form.CardTemplateJson && form.CardTemplateJson.length > 0){
            let cardFormAnswersObj = JSON.parse(cfFormAnswer);
            formatInputRawData(JSON.parse(form.CardTemplateJson)?.body, cardFormAnswersObj);
            cfFormAnswer = JSON.stringify(cardFormAnswersObj);
            form.CardAnswerJson = cfFormAnswer;
          }
        }
      });
      ticketData.Entity.CustomForms = customForms;
    }

    if(ticketData.Entity.CardAnswerJson && ticketData.Entity.CardAnswerJson.length > 0){
      cardFormAnswers = ticketData.Entity.CardAnswerJson;
      if (ticketData.Entity.CardTemplateJson && ticketData.Entity.CardTemplateJson.length > 0) {
        let cardFormAnswersObj = JSON.parse(cardFormAnswers);
        formatInputRawData(JSON.parse(ticketData.Entity.CardTemplateJson)?.body, cardFormAnswersObj);
        cardFormAnswers = JSON.stringify(cardFormAnswersObj);
        ticketData.Entity.CardAnswerJson = cardFormAnswers;
      }
    }

    if (ticketData.SelectedTemplate.CardFileAttachments && ticketData.SelectedTemplate.CardFileAttachments.length > 0) {
      updateCFviaFileUpload = true;
      cardFileAttachments = ticketData.SelectedTemplate.CardFileAttachments;
      delete ticketData.SelectedTemplate.CardFileAttachments;

      if (cardFormAnswers.length > 0 && cardFormAnswers.includes("Ticket."))
        delete ticketData.Entity.CardAnswerJson;
      if (ticketData.Entity.CustomForms?.length>0)
        delete ticketData.Entity.CustomForms;
    }
    else if (cardFormAnswers.length > 0 && cardFormAnswers.includes("Ticket.")) {
      updateCFAnswers = true;
      delete ticketData.Entity.CardAnswerJson;
      delete ticketData.Entity.CustomForms;
    }else if (ticketData.Entity.CustomForms?.length>0){
      updateCFAnswers = true;
      delete ticketData.Entity.CustomForms;
    }

    try {
      let haveResolution = false;
      let tempResolution = null;
      if (resolutionNoteEnabled && tikitConfig.IsResolutionNoteEnabled && ticketData.Entity.hasOwnProperty('Resolution')) {
        if (ticketData.Entity.StatusId == resolvedStatusId) {
          haveResolution = true;
          tempResolution = clone(ticketData.Entity.Resolution);
          delete ticketData.Entity.Resolution.ResolutionCategory;
        }
        else delete ticketData.Entity["Resolution"];
      }

      if (ticketData.Entity.hasOwnProperty('UpdatedTicket')) delete ticketData.Entity.UpdatedTicket;
      
      ticketData.Entity.CustomForms = customForms;
      const result: any = await UnitOfWork.AddTicket(ticketData);
      if (haveResolution) {
        if (isResolutionRole && ticketData.Entity.Resolution.DraftKB) {
          ticketData.Entity.Id = result.ticket.Id;
          let kbId = await AddOrUpdateResolutionToDraftKB(tempResolution, ticketData.Entity, currentState, t);
          api.updateTicketResolutionNote(result.ticket.ResolutionId, { AnswerId: kbId })
        }
      }

      if (result.ticket !== null) {
        let ticketId = result.ticket.Id;
        await Promise.all([
          t
        ].filter(o => o !== null));

        if (updateCFAnswers || updateCFviaFileUpload) {
          await Promise.all([
            (updateCFviaFileUpload) ? UnitOfWork.UploadTicketFiles(ticketId, customForms, cardFileAttachments, false, true) : null,
          ].filter(o => o !== null));
        }

        if (ticketData.LifecycleId) {
            ticketData.SelectedTemplate.Lifecycle.Phases.map(x => {
              x["IsCurrent"] = x.Order == 0;
            });
            result.ticket["TicketLifecycle"] = ticketData.SelectedTemplate.Lifecycle;
          
        }

        if (createGroupChat) {
          let userIds = [
            result.ticket.RequesterId,
            affectedUsers?.map((x: { Id: number; }) => x.Id),
            collaboratorsEmails?.map((x: { Id: number; }) => x),
            result.ticket.AssigneeId
          ].filter(x => x != null);
          await api.createGroupChat(result.ticket.Id, [...new Set(userIds.flat())]);
        }

        updateBoard(result.ticket, result.requester, transactionId);
        ticketId = result.ticket.Id;
        Toast.success(t('ticket.add-ticket.ticket-added', { ticketId: ticketId }));
        

        if (!ticketData.SelectedTemplate.NoCustomForm && ticketData.SelectedTemplate.ClearFormAnswer == true) {
          let templateId = ticketData.SelectedTemplate.Id;
          let sendFormToId = "";
          let sendFormViaTVA = true;
         
          switch(ticketData.SelectedTemplate.SendCustomFormSettings.Type){
            case SEND_FORM_OPTION.SEND_TO_REQUESTER:
              sendFormToId = ticketData.Entity.RequesterId;
              break;
            case SEND_FORM_OPTION.SEND_TO_ASSIGNEE:
              sendFormToId = ticketData.Entity.AssigneeId
              sendFormViaTVA = false;
              break;
            case SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER:
              sendFormToId = ticketData.SelectedTemplate.SendCustomFormSettings.SelectedAffectedUserId;
              break;
            case SEND_FORM_OPTION.SEND_TO_COLLABORATOR:
              sendFormToId = ticketData.SelectedTemplate.SendCustomFormSettings.SelectedCollaboratorId;
              sendFormViaTVA = false;
              break;
          }

          await api.sendCustomFormToConversation({ 
            TicketId: ticketId, 
            TemplateId: templateId, 
            UserId: sendFormToId, 
            IsTVA: sendFormViaTVA, 
            IsShowTyping: true,
            IsSendWithCustomAnswer: currentState.customFormState.isSendCurrentAnswers,
            TenantId: currentState?.teamsConfig?.TenantId});
        }
      } else {
        errorHandler(transactionId);
      }
    } catch (e) {
      console.error(e);
      errorHandler(transactionId);
    }

    if (ticketId)
    {
      dispatch({ action: TicketActions.ClearData });
      setSelectedTemplate({});
    }
  }

  const closeApplyTemplate = () => {
    setOpenTemplateList(false);
    dispatch({ action: "TemplateHasError", data: false });
    dispatch({ action: "TemplateErrorMessage", data: "" });
  };

  const applyTemplate = () => {
    setOpenTemplateList(false);

    setState((_state: AppStateValue) => {
        const updatedData = { "SelectedTemplate": clone(ticketData.SelectedTemplate) };
        if (!ticketData.SelectedTemplate.NoCustomForm && !ticketData.SelectedTemplate.ClearFormAnswer) {
          ticketData.SelectedTemplate.CustomForms.forEach(customForm=>{
              mapCardAnswers(updatedData["SelectedTemplate"], JSON.stringify(customForm.CardTemplateJson), JSON.stringify(customForm.CardAnswerJson), currentState, ticketData);
          });

          if (updatedData.SelectedTemplate.RequesterPicker) {
            const picker1: any = document.getElementById('mgt-requester');
            if (picker1)
              picker1.selectedPeople = [updatedData.SelectedTemplate.RequesterPicker].map((i: any) => ({ displayName: i.FullName, userPrincipalName: i.UserName, id: i.AadObjectId }));

            dispatch({ action: TicketActions.Requester, isEntity: false, data: updatedData.SelectedTemplate.RequesterPicker });
            if (updatedData.SelectedTemplate.RequesterPicker)
              delete updatedData.SelectedTemplate.RequesterPicker;
          }

          if (updatedData.SelectedTemplate.TagsPicker && updatedData.SelectedTemplate.TagsPicker.length > 0) {
            dispatch({ action: TicketActions.Tags, isEntity: false, data: updatedData.SelectedTemplate.TagsPicker });
            delete updatedData.SelectedTemplate.TagsPicker;
          }
      }

      for (let prop in ticketData.SelectedEnums) {
        let listItem = [];
        switch (prop) {
          case TicketActions.StatusId:
            listItem = _state.ticketStatus;
            break;
          case TicketActions.CategoryId:
            listItem = _state.categories;
            break;
          case TicketActions.PriorityId:
            listItem = _state.priority;
            break;
          case TicketActions.TicketTypeId:
            listItem = _state.ticketTypes;
            break;
        }

        dispatch({
          action: TicketActions.SelectedEnums,
          property: prop,
          data: listItem.find(x => x.Id == updatedData.SelectedTemplate[prop])
        });
      }

      dispatch({ action: TicketActions.TemplateId, isEntity: true, data: updatedData.SelectedTemplate[TicketActions.Id] });

      let title = updatedData.SelectedTemplate[TicketActions.Title];
      if (title != null)
        dispatch({ action: TicketActions.Title, isEntity: true, data: title });

      let dueDate = updatedData.SelectedTemplate[TicketActions.DueDate];
      let relativeDate = updatedData.SelectedTemplate[TicketActions.RelativeDueDate];
      if (dueDate != null || relativeDate != null) {
        if (dueDate == null && relativeDate != null)
          dueDate = getRelativeDateValue(relativeDate).toISO();
        else if (!dueDate.includes("T"))
          dueDate = getCardDateInputISOFormatted(dueDate);
        dispatch({ action: TicketActions.DueDate, isEntity: true, data: (dueDate != null ? new Date(dueDate) : null) });
      }

      let assignee = updatedData.SelectedTemplate[TicketActions.AssigneeId];
      if (assignee != null)
        dispatch({ action: TicketActions.AssigneeId, isEntity: true, data: assignee });

      let supportId = updatedData.SelectedTemplate[TicketActions.SupportGroupId];
      if (supportId != null)
        dispatch({ action: TicketActions.SupportGroupId, isEntity: true, data: supportId });

      let teamId = updatedData.SelectedTemplate[TicketActions.TeamId];
      if (teamId != null)
        dispatch({ action: TicketActions.TeamId, isEntity: true, data: teamId });

      let lifeCycleId = ticketData.SelectedTemplate[TicketActions.LifecycleId];
      if (lifeCycleId != null)
        dispatch({ action: TicketActions.LifecycleId, isEntity: false, data: lifeCycleId });

      if ((ticketData.SelectedTemplate.AffectedUsers && ticketData.SelectedTemplate.AffectedUsers.length > 0) ||
        (updatedData.SelectedTemplate.AffectedUsersPicker && updatedData.SelectedTemplate.AffectedUsersPicker.length > 0)) {
        const aUsers = updatedData.SelectedTemplate.AffectedUsersPicker || ticketData.SelectedTemplate.AffectedUsers;
        const picker: any = document.getElementById('mgt-affected-users');
        if (picker)
          picker.selectedPeople = aUsers.map((i: any) => ({ displayName: i.FullName, userPrincipalName: i.UserName, id: i.AadObjectId }));

        dispatch({ action: TicketActions.AffectedUsers, isEntity: true, data: aUsers });
        if (updatedData.SelectedTemplate.AffectedUsersPicker)
          delete updatedData.SelectedTemplate.AffectedUsersPicker;
      }

      if ((ticketData.SelectedTemplate.Collaborators && ticketData.SelectedTemplate.Collaborators.length > 0) ||
        (updatedData.SelectedTemplate.CollaboratorsPicker && updatedData.SelectedTemplate.CollaboratorsPicker.length > 0)) {
        const selectedCollabs = updatedData.SelectedTemplate.CollaboratorsPicker || ticketData.SelectedTemplate.Collaborators;
        dispatch({ action: TicketActions.Collaborators, isEntity: true, data: selectedCollabs.map(u => u.Id) });
        if (updatedData.SelectedTemplate.CollaboratorsPicker)
          delete updatedData.SelectedTemplate.CollaboratorsPicker;
      }

      if (!ticketData.SelectedTemplate.NoCustomForm) {
        if (ticketData.SelectedTemplate.ClearFormAnswer)
          ticketData.SelectedTemplate[TicketActions.CardAnswerJson] = "";

        dispatch({ action: TicketActions.CardTemplateJson, isEntity: true, data: ticketData.SelectedTemplate[TicketActions.CardResponseJson] });
        dispatch({ action: TicketActions.CardAnswerJson, isEntity: true, data: buildCardAnswerJSON(ticketData.SelectedTemplate.CustomForms)});

        let customFormList = [];
        if (ticketData.SelectedTemplate.CustomForms && ticketData.SelectedTemplate.CustomForms.length > 0) {
           ticketData.SelectedTemplate.CustomForms.filter(x=>x.IsAnswered).forEach(form =>{
              customFormList.push({TemplateFormId: form.TemplateFormId, FormName: form.Name, Order: form.Order, CardTemplateJson: JSON.stringify(form.CardTemplateJson), CardAnswerJson: JSON.stringify(form.CardAnswerJson)  });
           }); 
           dispatch({ action: TicketActions.CustomForms, isEntity: true, data: customFormList });
        }

      } else {
        dispatch({ action: TicketActions.CardTemplateJson, isEntity: true, data: null });
        dispatch({ action: TicketActions.CardAnswerJson, isEntity: true, data: null });
      }

      if(ticketData.SelectedTemplate.CustomPropertiesAnswerJson || updatedData.SelectedTemplate.CustomPropertiesAnswerJson){
        const customPropsAnswer = updatedData.SelectedTemplate.CustomPropertiesAnswerJson || ticketData.SelectedTemplate.CustomPropertiesAnswerJson;
        dispatch({action:TicketActions.CustomPropertiesAnswerJson, isEntity:true, data: customPropsAnswer});
      }

      dispatch({action:TicketActions.ApplyTemplateNow, data: true});
      //Apply selected template for show name of the selected template on the dialog  
      setIsCustomFormAnswersAccordionOpen(false);
      setSelectedTemplate(updatedData.SelectedTemplate);

      return {
        ..._state
      };
    });

  }

  const resetForm = () => {
    dispatch({ action: TicketActions.ClearData });
    dispatch({ action: TicketActions.ResetForm, data: true });
    setSelectedTemplate({});

    let picker: any = document.getElementById('mgt-affected-users');
    if (picker) picker.selectedPeople = [];

    picker = document.getElementById('mgt-requester');
    if (picker) picker.selectedPeople = [];
  }
  
  return (
    <>
      <Dialog
        className={`responsive-dialog top-0 fixed md:relative max-width-600 z-10 mobile-full`}
        style={{ overflowX: 'hidden' }}
        closeOnOutsideClick={false}
        open={showAddTicketDialog}
        onOpen={onOpen}
        content={
          <div className={`${AppCSS['addTicketDialogForm']}`}>

            {(!isCustomPropertiesEnabled || (isCustomPropertiesEnabled && currentState?.customProperties)) && (
              <TicketForm type={ActionTypes.Create} data={ticketData} dispatch={dispatch} setShowResolvedDialog={setShowResolvedDialog}
              showResolvedDialog={showResolvedDialog}
              submitResolveDialog={async (data)=>{
                ticketData.Entity.Resolution=data;
                await createTicket();
              }} />
            )}
            {((ticketData?.Entity?.CustomForms?.length ?? 0) > 0 && !ticketData.SelectedTemplate.ClearFormAnswer
             && ticketData?.Entity?.TemplateId == ticketData.SelectedTemplate.Id) && (
              <Flex column gap="gap.medium">
                 <EditTicketCustomFormAnswers
                    globalTheme={globalTheme}
                    ticketId={ticketData?.Entity?.Id}
                    isOpen={isCustomFormAnswersAccordionOpen}
                    onCLose={value => {setIsCustomFormAnswersAccordionOpen(value)}}
                    primaryButtonColor={globalTheme.siteVariables.colorScheme.brand.foreground1}
                    isSaving={false}
                    parentData={ticketData?.Entity}
                    isDisabled={true}
                    showUpdateAnswerButton={false}
                    loadingTicketData={false}
                  />
              </Flex>
            )}
          </div>
        }
        header={{
          className: 'w-full',
          // Held to the dialog's width (Northstar sizes the header to its content), so a long template name ends in an
          // ellipsis rather than widening the header past the dialog.
          styles: { minWidth: 0, maxWidth: '100%' },
          content: (
            <>
              <div className="flex items-center justify-between gap-2">
                <h3 className="flex-shrink-0">{t('ticket-details.edit-ticket-view.new-ticket')}</h3>
                <div className="flex min-w-0 add-ticket-header-actions">
                  {selectedTemplate?.LifecycleId && <Button disabled text icon={<LifecycleIcon />} content={selectedTemplate?.Lifecycle?.Title} className={`${AppCSS.navActionButton} ${AppCSS.lifecycleIconButton} button-width-max`} style={{ color: globalTheme.siteVariables.colorScheme.default.foreground2, }} />}
                  <Button primary text icon={<FilledFileIcon />} content={selectedTemplate?.Name ? selectedTemplate?.Name : t('ticket-details.edit-ticket-view.apply-template')} className={`${AppCSS.navActionButton} button-width-max`} style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, }} onClick={() => setOpenTemplateList(true)} />
                  <Button primary text icon={<RefreshIcon />} content={t('ticket-details.edit-ticket-view.reset')} aria-label={t('ticket-details.edit-ticket-view.reset')} title={t('ticket-details.edit-ticket-view.reset')} className={`${AppCSS.navActionButton} add-ticket-reset`} style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, }} onClick={() => resetForm()} />
                </div>
              </div>
            </>
          ),
        }}
        footer={
          <>
            <span className={`${AppCSS.buttonRowButton}`}>
              <Button content={t('ticket-details.edit-ticket-view.cancel')} onClick={() => { setShowAddTicketDialog(false); }} />
            </span>
            {isGroupChatEnabled && (
              <span className={`${AppCSS.buttonRowButton}`}>
                <Button content={t('group-chat.create')} secondary className={`${AppCSS.buttonRowButton}`}
                  disabled={missingRequired}
                  onClick={() => {
                    setCreateGroupChat(true);
                  }} />
              </span>
            )}
            <span className={`${AppCSS.buttonRowButton}`}>
              <Button content={t('ticket-details.edit-ticket-view.create')} primary disabled={missingRequired} className={`${AppCSS.buttonRowButton}`} onClick={() => { submit(); }} />
            </span>
          </>
        }
        trigger={
          <Button
            primary
            text
            icon={<AddIcon size='small' />}
            title='Add'
            content={t('ticket-details.edit-ticket-view.add-ticket')}
            onClick={() => {
              setShowAddTicketDialog(true);
            }}
            className={`${AppCSS.navActionButton} mr-2`}
            disabled={!isUserLicensed || isSaving}
            style={{ color: toolbarButtonColor(isUserLicensed && !isSaving, globalTheme.siteVariables.colorScheme) }}
          />
        }
      />
      <TemplateListDialog
        isNewTicket={true}
        formState={ticketData}
        showTemplate={openTemplateList}
        isApplyingTemplateWithLifecycle={false}
        templateList={templateList}
        templateListLoaded={templateListLoaded}
        dataSourceList={dataSourceList}
        dispatch={dispatch}
        closeApplyTemplate={closeApplyTemplate}
        applyTemplate={applyTemplate}
        ticketApprovals={[]}
      />
    </>
  );
};
